import React from 'react';
import { ListChecks } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { MigracionPaso } from './MigracionPaso';
import { MigracionResultadoGenerico } from './MigracionResultadoGenerico';
import { DiagnosticoCvusAnteriores } from './DiagnosticoCvusAnteriores';

const ADMIN = ['ADMIN', 'SUPERADMIN', 'ADMINISTRADOR'];

const PASOS = [
  { letra: '03', tipo: 'cvus_duplicados', titulo: '0.3 CVU duplicados',
    texto: 'Unifica el mismo CVU repetido dentro de un cliente: conserva la entrada más completa y completa sus datos vacíos con las otras copias. Nunca reactiva: si alguna copia está inactiva, queda inactivo. Las copias quitadas quedan guardadas en cvus_unificados del cliente. Backup: padron_interno.' },
  { letra: 'B', tipo: 'limpieza_monitores', titulo: 'Limpieza de monitores',
    texto: 'Crea los 4 monitores nuevos, desactiva los reemplazados (sin borrarlos), ajusta los de vínculos y los grupos, y crea los parámetros de horario nocturno y señales. Backup: monitors, monitor_groups, system_config.' },
  { letra: 'C', tipo: 'waya_vivo', titulo: 'Monitor Waya en vivo',
    texto: 'Ajusta las reglas automáticas (ventana de 24 h, regla 3 de CIERRE a BLOQUEO con el horario nocturno) y archiva las alertas pendientes que solo son señales. Backup: waya_auto_reglas, waya_alertas_vivo.' },
  { letra: 'D', tipo: 'cuentas_sin_identificar', titulo: 'Cuentas sin identificar',
    texto: 'Carga inicial desde el histórico: agrega al padrón los CVU con CUIT válido (crea el cliente si no existe) y registra las cuentas sin identificar. Backup: padron_interno, cuentas_sin_identificar.' },
  { letra: 'E', tipo: 'estado_usuario_waya', titulo: 'Estado de usuario Waya',
    texto: 'Copia a "estado de usuario Waya" el Activo / Eliminado que el Reporte de usuarios dejó en estado_waya (no lo borra). Backup: padron_interno.' },
  { letra: 'F', tipo: 'lista_blanca', titulo: 'Lista blanca',
    texto: 'Pasa cada entrada vieja a alcance TODOS, sin vencimiento (las ya vencidas conservan su fecha), con su CUIT resuelto por padrón. Lista las que no se pudieron resolver. Backup: account_lists.' },
];

// Monitores → "Migraciones B a F" (refactor de monitores): vista previa (solo lee) y ejecutar con EJECUTAR + backup.
export const MigracionesRefactorAccion = () => {
  const { user } = useAuth();
  const [open, setOpen] = React.useState(false);
  if (!ADMIN.includes((user?.rol || '').toUpperCase())) return null;
  return (
    <>
      <Button variant="outline" onClick={() => setOpen(true)} data-testid="migraciones-refactor-btn">
        <ListChecks size={16} className="mr-2" /> Migraciones (0 y B a F)
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto overflow-x-hidden" data-testid="migraciones-refactor-dialog">
          <DialogHeader>
            <DialogTitle>Migraciones del refactor de monitores</DialogTitle>
            <DialogDescription>
              Correlas en orden. La vista previa solo lee; ejecutar hace primero el backup y pide escribir EJECUTAR.
              Se pueden repetir: lo que ya se aplicó no se vuelve a cambiar.
            </DialogDescription>
          </DialogHeader>
          {open && (
            <Tabs defaultValue="01">
              <TabsList className="flex flex-wrap h-auto justify-start">
                <TabsTrigger value="01" data-testid="migraciones-tab-01">0.1 Diagnóstico CVU</TabsTrigger>
                {PASOS.map((p) => (
                  <TabsTrigger key={p.letra} value={p.letra} data-testid={`migraciones-tab-${p.letra}`}>{p.letra === '03' ? p.titulo : `${p.letra}. ${p.titulo}`}</TabsTrigger>
                ))}
              </TabsList>
              <TabsContent value="01" className="pt-3 space-y-3">
                <p className="text-sm text-slate-600">
                  Solo lectura. CVU de bancos sponsor anteriores (todos menos BI): cuántos inactivó la última reconciliación,
                  qué huella tienen (qué proceso los había dejado activos) y qué procesos corrieron entre las dos últimas reconciliaciones.
                </p>
                <DiagnosticoCvusAnteriores />
              </TabsContent>
              {PASOS.map((p) => (
                <TabsContent key={p.letra} value={p.letra} className="pt-3 space-y-3">
                  <p className="text-sm text-slate-600">{p.texto}</p>
                  <MigracionPaso tipo={p.tipo} prefijo={`migracion-${p.letra}`} textoEjecutar={`Ejecutar ${p.letra}`}>
                    {(r) => <MigracionResultadoGenerico r={r} />}
                  </MigracionPaso>
                </TabsContent>
              ))}
            </Tabs>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
